// ConfirmSummary.tsx — widget del step 'confirm'.
// Qué hace: resumen de la tirada (plataformas + cantidad, estilo de imagen,
//   formato, prompt) + botón generar + "change something" (reset).
// Qué exporta: ConfirmSummary.
// Quién lo usa: NovahGenerateChat.

import { SocialNetwork, PreviewFormat, PREVIEW_FORMATS } from '@agency-smm/shared';
import { ImageStyle, PLATFORMS, suggestFormat } from '../types';
import { TopicDraft } from '../useTopicsDraft';
import { WidgetWrap, SummaryCard, ConfirmBtn, SkipBtn } from '../styled';

interface Props {
  selectedPlatforms: Set<SocialNetwork>;
  /** Temas cargados, con su cupo por plataforma. Vacío = "surprise me". */
  topics: TopicDraft[];
  /** Suma de los cupos de los temas (por plataforma). */
  topicDemand: number;
  /** Lo que se eligió en el paso de cantidad — hoy es un presupuesto sugerido. */
  budgetPerPlatform: number;
  /** Lo que finalmente se genera por plataforma: max(presupuesto, temas). */
  effectivePerPlatform: number;
  canPickStyle: boolean;
  imageStyle: ImageStyle;
  selectedStyleIds: string[];
  // "Foto intacta" activo (dentro del flujo AI-styled) → mostramos que la foto se
  // deja tal cual + texto encima, no un poster full-IA. Ticket 72968892.
  keepPhotoIntact?: boolean;
  selectedPhotoCount?: number;
  previewFormat: PreviewFormat;
  totalPosts: number;
  triggerGenerate: () => void;
  resetFlow: () => void;
}

export function ConfirmSummary({
  selectedPlatforms, topics, topicDemand, budgetPerPlatform, effectivePerPlatform,
  canPickStyle, imageStyle, selectedStyleIds,
  keepPhotoIntact, selectedPhotoCount, previewFormat, totalPosts, triggerGenerate, resetFlow,
}: Props) {
  // Los temas pidieron más que el paso de cantidad → se genera lo que piden los
  // temas y se avisa. Nunca bloquea, igual que el aviso de formato de abajo.
  const topicOverflow = topicDemand > budgetPerPlatform;
  // Con muchos temas la tarjeta se volvería un scroll: se muestran 6 y el resto
  // se resume en una línea.
  const shownTopics = topics.slice(0, 6);
  const intact = imageStyle === 'styled' && keepPhotoIntact && (selectedPhotoCount ?? 0) > 0;
  // Hint suave: si el formato elegido no es el que las redes seleccionadas sugieren,
  // avisamos sin bloquear (un 9:16 en el Feed de Instagram sale recortado, etc).
  const suggested = suggestFormat(Array.from(selectedPlatforms));
  const formatMismatch = selectedPlatforms.size > 0 && previewFormat !== suggested;

  return (
    <WidgetWrap>
      <SummaryCard>
        {Array.from(selectedPlatforms).map((p) => {
          const info = PLATFORMS.find((x) => x.id === p);
          return <div key={p}>{info?.icon} <strong>{effectivePerPlatform}</strong> {info?.label} posts</div>;
        })}
        <div>🖼️ {!canPickStyle ? 'Images: auto-selected for you' : imageStyle === 'transfer' ? 'AI Transfer — reference + your photo' : intact ? `My photo${(selectedPhotoCount ?? 0) !== 1 ? 's' : ''}, kept as-is + text on top` : imageStyle === 'mix' ? 'Stock + Solid mix' : imageStyle === 'stock' ? 'Stock photos' : imageStyle === 'pastel' ? 'Solid colors' : imageStyle === 'ai' ? 'AI Generated' : imageStyle === 'styled' ? `AI-styled posters (${selectedStyleIds.length})` : 'My photos'}</div>
        <div>📐 {PREVIEW_FORMATS[previewFormat].label}</div>
        {shownTopics.map((t) => (
          <div key={t.id}>💡 {t.topic} · <strong>{t.count}</strong> per platform</div>
        ))}
        {topics.length > shownTopics.length && (
          <div>💡 +{topics.length - shownTopics.length} more topics</div>
        )}
        <div style={{ marginTop: 4 }}>✨ <strong>{totalPosts} posts</strong> · included in your plan</div>
      </SummaryCard>
      {topicOverflow && (
        <div style={{ fontSize: 11, color: '#b45309', marginTop: 6, lineHeight: 1.5 }}>
          ⚠️ Your topics ask for {topicDemand} posts per platform — {topicDemand - budgetPerPlatform} more
          than the {budgetPerPlatform} you picked. I'll generate all {totalPosts}.
        </div>
      )}
      {formatMismatch && (
        <div style={{ fontSize: 11, color: '#b45309', marginTop: 6, lineHeight: 1.5 }}>
          💡 {PREVIEW_FORMATS[previewFormat].label} may not be ideal for your networks — {PREVIEW_FORMATS[suggested].label} fits them better. You can still generate.
        </div>
      )}
      <ConfirmBtn onClick={triggerGenerate}>
        Generate {totalPosts} Posts
      </ConfirmBtn>
      <SkipBtn onClick={resetFlow}>Change something</SkipBtn>
    </WidgetWrap>
  );
}
